import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdDownload } from 'react-icons/md';
import Card from 'components/card/Card';
import { useFinance } from 'contexts/FinanceContext';
import { exportTransactionsToCsv } from 'lib/csv';
import {
  CURRENCIES,
  CurrencyCode,
  WALLET_CHAINS,
  WalletChain,
} from 'types/finance';

export default function Settings() {
  const {
    transactions,
    categories,
    totals,
    currency,
    setCurrency,
    usdtPriceBdt,
    setUsdtPriceBdt,
    wallets,
    addWallet,
    deleteWallet,
    cryptoRates,
  } = useFinance();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'gray.400');
  const boxBg = useColorModeValue('secondaryGray.300', 'navy.900');

  const [usdtInput, setUsdtInput] = useState(String(usdtPriceBdt || ''));
  const [label, setLabel] = useState('');
  const [chain, setChain] = useState<WalletChain>('trc20');
  const [address, setAddress] = useState('');

  const handleExport = () => {
    if (transactions.length === 0) {
      toast({
        title: 'No transactions to export',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    exportTransactionsToCsv(transactions, categories);
    toast({ title: 'CSV downloaded', status: 'success', duration: 3000 });
  };

  const handleCurrencyChange = (value: string) => {
    setCurrency(value as CurrencyCode);
    toast({
      title: 'Main currency updated',
      status: 'success',
      duration: 2500,
    });
  };

  const handleSaveUsdtPrice = () => {
    const price = Number(usdtInput);
    if (!Number.isFinite(price) || price <= 0) {
      toast({
        title: 'Enter a valid USDT price in BDT',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    setUsdtPriceBdt(price);
    toast({
      title: `USDT price set to ৳${price}`,
      description: 'Wallet transaction amounts recalculated in BDT.',
      status: 'success',
      duration: 3000,
    });
  };

  const handleAddWallet = () => {
    if (!address.trim()) {
      toast({ title: 'Enter a wallet address', status: 'warning', duration: 2500 });
      return;
    }
    if (chain === 'trc20' && !address.trim().startsWith('T')) {
      toast({
        title: 'TRON addresses usually start with T',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    if ((chain === 'bep20' || chain === 'erc20') && !address.trim().startsWith('0x')) {
      toast({
        title: 'EVM addresses should start with 0x',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    addWallet({
      label: label.trim() || WALLET_CHAINS.find((c) => c.id === chain)?.label || chain,
      chain,
      address: address.trim(),
    });
    setLabel('');
    setAddress('');
    toast({ title: 'Wallet added', status: 'success', duration: 2500 });
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px" mb="20px">
        <Card p="30px">
          <Text color={textColor} fontSize="xl" fontWeight="700" mb="20px">
            Settings
          </Text>
          <FormControl mb="20px">
            <FormLabel color={textColorSecondary} fontSize="sm" fontWeight="500">
              Main currency
            </FormLabel>
            <Select
              value={currency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              size="lg"
            >
              {CURRENCIES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code} ({item.symbol}) — {item.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl mb="12px">
            <FormLabel color={textColorSecondary} fontSize="sm" fontWeight="500">
              USDT price (BDT)
            </FormLabel>
            <Flex gap="10px">
              <Input
                type="number"
                value={usdtInput}
                onChange={(e) => setUsdtInput(e.target.value)}
                placeholder="e.g. 122"
                size="lg"
              />
              <Button
                variant="brand"
                borderRadius="16px"
                px="24px"
                onClick={handleSaveUsdtPrice}
              >
                Save
              </Button>
            </Flex>
          </FormControl>
          <Text color={textColorSecondary} fontSize="sm">
            Used only for wallet imports. TRX / BNB / ETH convert to USDT (live
            rates), then to BDT with this price.
          </Text>
          <Text color={textColorSecondary} fontSize="xs" mt="8px">
            Live USD: TRX ${cryptoRates.TRX || '—'} · BNB $
            {cryptoRates.BNB || '—'} · ETH ${cryptoRates.ETH || '—'}
          </Text>
        </Card>

        <Card p="30px">
          <Text color={textColor} fontSize="xl" fontWeight="700" mb="20px">
            Data
          </Text>
          <Flex direction="column" gap="12px" mb="24px">
            <Flex justify="space-between">
              <Text color={textColorSecondary}>Total transactions</Text>
              <Text color={textColor} fontWeight="600">
                {totals.transactionCount}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>Wallets</Text>
              <Text color={textColor} fontWeight="600">
                {wallets.length}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>USDT → BDT</Text>
              <Text color={textColor} fontWeight="600">
                ৳{usdtPriceBdt || 0}
              </Text>
            </Flex>
          </Flex>
          <Button
            leftIcon={<Icon as={MdDownload} />}
            colorScheme="brand"
            onClick={handleExport}
            w="100%"
          >
            Export Transactions (CSV)
          </Button>
        </Card>
      </SimpleGrid>

      <Card p="30px">
        <Text color={textColor} fontSize="xl" fontWeight="700" mb="8px">
          Wallets
        </Text>
        <Text color={textColorSecondary} fontSize="sm" mb="20px">
          Fetch only official USDT + native coin: TRC20 (USDT, TRX), BEP20
          (USDT, BNB), ERC20 (USDT, ETH).
        </Text>

        <SimpleGrid columns={{ base: 1, md: 4 }} gap="12px" mb="16px">
          <FormControl>
            <FormLabel fontSize="sm">Label</FormLabel>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="My TRON"
              bg={boxBg}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Chain</FormLabel>
            <Select
              value={chain}
              onChange={(e) => setChain(e.target.value as WalletChain)}
              bg={boxBg}
            >
              {WALLET_CHAINS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} — {c.tokens}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl gridColumn={{ md: 'span 2' }}>
            <FormLabel fontSize="sm">Address</FormLabel>
            <Flex gap="10px">
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={
                  chain === 'trc20' ? 'T...' : '0x...'
                }
                bg={boxBg}
              />
              <Button
                leftIcon={<Icon as={MdAdd} />}
                variant="brand"
                borderRadius="16px"
                onClick={handleAddWallet}
                flexShrink={0}
              >
                Add
              </Button>
            </Flex>
          </FormControl>
        </SimpleGrid>

        <Flex direction="column" gap="10px">
          {wallets.length === 0 ? (
            <Text color={textColorSecondary}>No wallets yet.</Text>
          ) : (
            wallets.map((w) => (
              <Flex
                key={w.id}
                align="center"
                justify="space-between"
                gap="12px"
                p="14px"
                bg={boxBg}
                borderRadius="16px"
              >
                <Box minW={0}>
                  <Text color={textColor} fontWeight="700" fontSize="sm">
                    {w.label}{' '}
                    <Text as="span" color="secondaryGray.600" fontWeight="500">
                      · {w.chain.toUpperCase()}
                    </Text>
                  </Text>
                  <Text
                    color={textColorSecondary}
                    fontSize="xs"
                    fontFamily="mono"
                    isTruncated
                  >
                    {w.address}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Delete wallet"
                  icon={<Icon as={MdDelete} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => {
                    deleteWallet(w.id);
                    toast({ title: 'Wallet removed', status: 'info', duration: 2000 });
                  }}
                />
              </Flex>
            ))
          )}
        </Flex>
      </Card>
    </Box>
  );
}
